import React, { useCallback, useRef, useState } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';

import useServerSideRendering from '@hooks/useServerSideRendering';
import { root_path, subscribe_promotion_path } from '@routes';
import type { FormikSubmitForm } from '@utils/types';

import { PrimaryButton } from '../Button/variants';
import TIModal from '../TIModal';
import StepBasicInfo from './StepBasicInfo';
import StepComplete from './StepComplete';
import StepEnableProfile from './StepEnableProfile';

const queryClient = new QueryClient();

export enum ModalHeading {
  ForumComment = 'Enable your profile to post to the forum',
  ArticleComment = 'Enable your profile to comment on this article',
  Subscriber = 'Enable your profile to connect with other subscribers',
  Lead = 'Subscribe to The Information',
}

export enum ModalSubheading {
  Comment = 'Join this discussion',
  Subscriber = 'Join the community',
  DirectoryLead = "Become a subscriber to view The Information's exclusive subscriber directory",
  ForumPostLead = 'Become a subscriber to publish a post',
}

export enum ModalLinkText {
  ForumComment = 'Enable profile & post to forum',
  ArticleComment = 'Enable profile & post comment',
  Subscriber = 'Turn on my profile',
  Lead = 'Subscribe & save 25%',
}

export enum ModalLinkUrl {
  Lead = subscribe_promotion_path({ offer: 'pmod-336' }),
}

export type ModalContent = {
  heading: ModalHeading;
  subheading: ModalSubheading;
  linkText: ModalLinkText;
  linkUrl?: ModalLinkUrl;
};

export type CommentData = {
  type: string;
  id: number;
  page_to_reload: string;
};

type EnableProfileModalProps = {
  isLoggedIn: boolean;
  isSubscriber: boolean;
  isTriggeredByComment?: boolean;
  commentButtonText?: string;
  isCommentForForum?: boolean;
  handleSubmitComment?: FormikSubmitForm;
  commentData?: CommentData;
  buttonClasses?: string;
  modalContent?: ModalContent;
};

const EnableProfileModal = ({
  isLoggedIn,
  isSubscriber,
  isTriggeredByComment,
  commentButtonText,
  isCommentForForum,
  handleSubmitComment,
  commentData, // TODO: remove when we render all comments from react components,
  modalContent,
}: EnableProfileModalProps) => {
  // don't need this state (can pass from parents) once this component is always rendered by react components
  const [isOpen, setIsOpen] = useState(!isTriggeredByComment);
  const [isSaving, setIsSaving] = useState(false);
  const [step, setStep] = useState(0);
  const [profileEnabled, setProfileEnabled] = useState(false);
  const triggerCommentRef = useRef<HTMLButtonElement>();
  const hydrated = useServerSideRendering();

  const onClose = useCallback(() => {
    const redirectOnClose = !isTriggeredByComment && !profileEnabled;
    if (hydrated) {
      return redirectOnClose ? (window.location = root_path()) : setIsOpen(false);
    }
  }, [isTriggeredByComment, profileEnabled, hydrated]);

  const currentStep = () => {
    switch (step) {
      case 0:
        return (
          <StepEnableProfile
            nextStep={() => setStep(1)}
            isLoggedIn={isLoggedIn}
            isSaving={isSaving}
            setIsSaving={setIsSaving}
            setProfileEnabled={setProfileEnabled}
            isTriggeredByComment={isTriggeredByComment}
            handleSubmitComment={handleSubmitComment}
            commentData={commentData} // TODO: remove once article comments render from react
            modalContent={
              modalContent ||
              (isSubscriber
                ? {
                    heading: isTriggeredByComment
                      ? isCommentForForum
                        ? ModalHeading.ForumComment
                        : ModalHeading.ArticleComment
                      : ModalHeading.Subscriber,
                    subheading: isTriggeredByComment
                      ? ModalSubheading.Comment
                      : ModalSubheading.Subscriber,
                    linkText: isTriggeredByComment
                      ? isCommentForForum
                        ? ModalLinkText.ForumComment
                        : ModalLinkText.ArticleComment
                      : ModalLinkText.Subscriber,
                  }
                : {
                    heading: ModalHeading.Lead,
                    subheading: ModalSubheading.DirectoryLead,
                    linkText: ModalLinkText.Lead,
                    linkUrl: ModalLinkUrl.Lead,
                  })
            }
            getCurrentTriggerComment={() => triggerCommentRef.current}
          />
        );
      case 1:
        return <StepBasicInfo nextStep={() => setStep(2)} />;
      case 2:
        return <StepComplete nextStep={onClose} />;
      default:
        onClose();
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      {isTriggeredByComment && (
        <PrimaryButton
          as="button"
          buttonRef={triggerCommentRef}
          type="button"
          size="small"
          className="min-w-[120px]"
          disabled={isSaving}
          onClick={(e) => {
            if (!profileEnabled) {
              e.preventDefault();
              setIsOpen(true);
            }
          }}
        >
          {commentButtonText}
        </PrimaryButton>
      )}
      <TIModal
        isOpen={isOpen}
        parentClass="EnableProfileModal"
        disableScrollLock={false}
        onClose={onClose}
      >
        {currentStep()}
      </TIModal>
    </QueryClientProvider>
  );
};

export default EnableProfileModal;
